import { type FC, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import confettiJson from '@assets/lottie/confetti.json';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Lottie from 'lottie-react';
import { useTimeout } from 'usehooks-ts';

import { playSound } from '@/common/lib/soundEffect';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { images } from '@/components/Contract/ContractRewardItem/config';
import { ModalContext } from '@/components/Modals/Modal/config';
import { MotionPicture } from '@/components/ui/Picture/Picture';

import { ContractRewardMotion } from '../ContractRewardMotion/ContractRewardMotion';

import { CompleteContractAnimationContext } from './context';

import css from './styles.module.scss';

import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';
import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';
import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';

interface CompleteContractAnimationProps {
  cash: number;
  point: number;
  dice?: number;
  silverTicket?: number;
  goldTicket?: number;
  diamondTicket?: number;
  bingoBalls?: number;
}

const CompleteContractAnimation: FC<CompleteContractAnimationProps> = (
  props,
) => {
  const {
    cash,
    point,
    dice,
    silverTicket,
    goldTicket,
    diamondTicket,
    bingoBalls,
  } = props;
  const { setOverlayIsActive } = useContext(ModalContext);
  const {
    isAnimationStart,
    animationStep,
    setIsAnimationStart,
    setAnimationStep,
  } = useContext(CompleteContractAnimationContext);
  const { t } = useTranslation();
  const nextStepTimerId = useRef<NodeJS.Timeout | undefined>();

  useTimeout(
    () => {
      setAnimationStep(2);
      setIsAnimationStart(false);
      setOverlayIsActive(false);
    },
    isAnimationStart ? 1000 : null,
  );

  useEffect(() => {
    return (): void => {
      clearTimeout(nextStepTimerId.current);
    };
  }, []);

  useEffect(() => {
    if (isAnimationStart) playSound('win/big');
  }, [isAnimationStart]);

  const countOfRewards = Object.entries(props).reduce(
    (count, [name, value]) => (name && value ? count + 1 : count),
    0,
  );

  const containerSize = countOfRewards < 4 ? 'size-24' : 'size-16';

  const ticket =
    silverTicket || goldTicket || diamondTicket
      ? {
          type: silverTicket
            ? 'silverTicket'
            : goldTicket
              ? 'goldTicket'
              : 'diamondTicket',
          value: (silverTicket || goldTicket || diamondTicket) as number,
        }
      : null;

  const ticketText: Record<string, string> = {
    silverTicket: 'Silver ticket',
    goldTicket: 'Gold ticket',
    diamondTicket: 'Diamond ticket',
  };

  return (
    <AnimatePresence>
      {isAnimationStart && (
        <motion.div
          exit={{ opacity: 0, transition: { duration: 0.4, ease: 'easeOut' } }}
          className={classNames(
            'absolute top-0 left-0 w-full h-full flex items-center justify-center z-[9999]',
            countOfRewards < 4 ? 'gap-10' : 'gap-4',
          )}
        >
          {!!cash && (
            <ContractRewardMotion
              motionProps={{
                layoutId: 'cash-reward',
                transition: {
                  duration: 0.5,
                  ease: 'easeOut',
                },
              }}
              classNames={{
                description: 'uppercase',
              }}
              title={toShortNumFormat(cash)}
              description={t('Cash')}
            >
              <div className={containerSize}>
                {animationStep === 1 && (
                  <MotionPicture
                    layoutId="contract-complete-cash"
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                    className={containerSize}
                    img={cashPng}
                    webp={cashWebp}
                    alt="cash"
                  />
                )}
              </div>
            </ContractRewardMotion>
          )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={css.blur}
          ></motion.div>
          <Lottie
            loop={false}
            className="w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            animationData={confettiJson}
          />
          {!!point && (
            <ContractRewardMotion
              motionProps={{
                layoutId: 'point-reward',
                transition: { duration: 0.5, ease: 'easeOut' },
              }}
              classNames={{
                description: 'uppercase',
              }}
              title={point}
              description={t('Star Points')}
            >
              <div className={containerSize}>
                {animationStep === 1 && (
                  <MotionPicture
                    layoutId="contract-complete-point"
                    className={containerSize}
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                    img={starPng}
                    webp={starWebp}
                    alt="point"
                  />
                )}
              </div>
            </ContractRewardMotion>
          )}
          {!!dice && (
            <ContractRewardMotion
              motionProps={{
                layoutId: 'dice-reward',
                transition: { duration: 0.5, ease: 'easeOut' },
              }}
              classNames={{
                description: 'uppercase',
              }}
              title={dice}
              description={t('Dice')}
            >
              <div className={containerSize}>
                {animationStep === 1 && (
                  <MotionPicture
                    layoutId="contract-complete-dice"
                    className={containerSize}
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                    img={dicePng}
                    webp={diceWebp}
                    alt="dice"
                  />
                )}
              </div>
            </ContractRewardMotion>
          )}
          {!!ticket && (
            <ContractRewardMotion
              motionProps={{
                layoutId: 'silver-ticket-reward',
                transition: { duration: 0.5, ease: 'easeOut' },
              }}
              classNames={{
                description: 'uppercase',
              }}
              title={ticket.value}
              description={t(ticketText[ticket.type])}
            >
              <div className={containerSize}>
                {animationStep === 1 && (
                  <MotionPicture
                    layoutId="contract-complete-silver-ticket"
                    className={containerSize}
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                    {...images[ticket.type as keyof typeof images]}
                    alt={ticket.type}
                  />
                )}
              </div>
            </ContractRewardMotion>
          )}
          {!!bingoBalls && (
            <ContractRewardMotion
              motionProps={{
                layoutId: 'bingo-balls-reward',
                transition: { duration: 0.5, ease: 'easeOut' },
              }}
              classNames={{
                description: 'uppercase',
              }}
              title={bingoBalls}
              description={t('Bingo Balls')}
            >
              <div className={containerSize}>
                {animationStep === 1 && (
                  <motion.img
                    layoutId="contract-complete-bingo-balls"
                    src="/img/bingo/bingo-icon.png"
                    alt=""
                    className={`${containerSize} object-contain`}
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                  />
                )}
              </div>
            </ContractRewardMotion>
          )}
        </motion.div>

        // <AnimatePresence>
        //   {isAnimationStart && (
        //     <motion.div
        //       initial={{ opacity: 0 }}
        //       animate={{ opacity: 1 }}
        //       className="fixed top-0 left-0 w-full h-full bg-black/80 z-9"
        //     >

        //     </motion.div>
        //   )}
        // </AnimatePresence>
      )}
    </AnimatePresence>
  );
};

export { CompleteContractAnimation };
